import './UploadSection.css';

import { useEffect, useRef, useState } from 'react';
import { beginScanSession, captureProductEvent } from '../utils/productAnalytics';

const UploadSection = ({
  selectedImage,
  previewUrl,
  showCamera,
  stream,
  loading,
  imageCompressing,
  onImageSelect,
  onStartCamera,
  onCaptureImage,
  onStopCamera,
  onClearImage,
  videoRef,
  canvasRef,
}) => {
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [torchEnabled, setTorchEnabled] = useState(false);
  const [cameraCapabilities, setCameraCapabilities] = useState(null);
  const isDefaultPreview = previewUrl === '/mejicana.jpeg' && !selectedImage;
  const hasUploadedPreview = Boolean(selectedImage && previewUrl && !showCamera);

  // Get camera capabilities when camera starts
  useEffect(() => {
    if (stream && stream.getVideoTracks().length > 0) {
      const track = stream.getVideoTracks()[0];
      const capabilities = track.getCapabilities();
      setCameraCapabilities(capabilities);
      console.log('Camera capabilities in component:', capabilities);
    }
  }, [stream]);

  const handleScanMenu = () => {
    beginScanSession({ source: 'camera', entryPoint: 'hero' });
    captureProductEvent('upload-source-clicked', { source: 'camera' });
    captureProductEvent('camera-opened', { source: 'camera' });
    onClearImage();
    if (cameraInputRef.current) {
      cameraInputRef.current.value = '';
    }
    cameraInputRef.current?.click();
  };

  const handleUploadFromGallery = () => {
    beginScanSession({ source: 'gallery', entryPoint: 'hero' });
    captureProductEvent('upload-source-clicked', { source: 'gallery' });
    captureProductEvent('file-picker-opened', { source: 'gallery' });
    onClearImage();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    fileInputRef.current?.click();
  };

  const toggleTorch = async () => {
    if (!stream || !cameraCapabilities?.torch) return;

    try {
      const track = stream.getVideoTracks()[0];
      const newTorchState = !torchEnabled;

      await track.applyConstraints({
        advanced: [{ torch: newTorchState }],
      });

      setTorchEnabled(newTorchState);
      console.log('Torch toggled:', newTorchState);
    } catch (error) {
      console.error('Error toggling torch:', error);
    }
  };

  const focusAtPoint = async (x, y) => {
    if (!stream || !cameraCapabilities?.focusMode) return;

    try {
      const track = stream.getVideoTracks()[0];

      // Try to set manual focus with a specific point of interest
      await track.applyConstraints({
        advanced: [
          {
            focusMode: 'manual',
            pointsOfInterest: [{ x: x / videoRef.current.videoWidth, y: y / videoRef.current.videoHeight }],
          },
        ],
      });

      // Switch back to continuous autofocus after a moment
      setTimeout(async () => {
        try {
          await track.applyConstraints({
            advanced: [{ focusMode: 'continuous' }],
          });
        } catch (err) {
          console.log('Could not return to continuous focus:', err);
        }
      }, 2000);

      console.log('Focus point set at:', x, y);
    } catch (error) {
      console.error('Error setting focus point:', error);
    }
  };

  const handleVideoClick = event => {
    if (!videoRef.current) return;

    const rect = videoRef.current.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    // Convert to video coordinates
    const videoX = (x / rect.width) * videoRef.current.videoWidth;
    const videoY = (y / rect.height) * videoRef.current.videoHeight;

    focusAtPoint(videoX, videoY);
  };

  return (
    <div className={`upload-section ${hasUploadedPreview ? 'has-uploaded-preview' : ''}`}>
      {previewUrl && !showCamera && (
        <div className="preview-section">
          <div className="preview-header">
            <div className="preview-title-group">
              <h3 className="preview-title">{isDefaultPreview ? 'How Menu AI works' : 'Your Menu Preview'}</h3>
              <span className="preview-reminder">
                {isDefaultPreview
                  ? 'Take a picture of your menu and get dish photos, nutrition, and allergen hints.'
                  : 'Upload readable, good resolution menu'}
              </span>
            </div>
            {!isDefaultPreview && (
              <button onClick={onClearImage} className="cancel-preview-btn" title="Remove menu preview">
                ✕
              </button>
            )}
          </div>
          <div className="preview-image-container">
            {isDefaultPreview ? (
              <video className="promo-preview-video" controls playsInline preload="metadata">
                <source src="/promo_video.mp4" type="video/mp4" />
              </video>
            ) : (
              <img src={previewUrl} alt="Menu Preview" className="preview-image" />
            )}
            {imageCompressing && (
              <div className="compression-overlay">
                <div className="compression-spinner">
                  <div className="spinner"></div>
                  <p>Improving Image...</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showCamera && (
        <div className="camera-section-fullscreen">
          <div className="camera-overlay">
            <div className="camera-controls-overlay">
              <button onClick={onStopCamera} className="camera-control-btn cancel-btn">
                ❌ Cancel
              </button>
              {cameraCapabilities?.torch && (
                <button
                  onClick={toggleTorch}
                  className={`camera-control-btn torch-btn ${torchEnabled ? 'torch-on' : ''}`}
                  title="Toggle flashlight"
                >
                  {torchEnabled ? '🔦' : '💡'} Flash
                </button>
              )}
              <button onClick={onCaptureImage} className="camera-control-btn capture-btn">
                📸 Capture
              </button>
            </div>
            <div className="camera-instructions">
              <p>Position your menu in the frame and tap capture</p>
              {cameraCapabilities?.focusMode && <p className="focus-instruction">💡 Tap anywhere to focus</p>}
            </div>
          </div>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="camera-video-fullscreen"
            onLoadedMetadata={() => console.log('Video metadata loaded')}
            onCanPlay={() => console.log('Video can play')}
            onPlay={() => console.log('Video started playing')}
            onError={e => console.error('Video error:', e)}
            onClick={handleVideoClick}
            style={{ cursor: cameraCapabilities?.focusMode ? 'crosshair' : 'default' }}
          />
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </div>
      )}

      {!showCamera && (
        <>
          <div className="upload-options-bottom">
            <button onClick={handleScanMenu} className="upload-image-button scan-button">
              <span className="camera-icon">📸</span>
              <span className="button-text">Scan Menu</span>
            </button>
            <button onClick={handleUploadFromGallery} className="upload-image-button gallery-upload-button">
              <span className="camera-icon">🖼️</span>
              <span className="button-text">Upload from Gallery</span>
            </button>
          </div>

          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={event => onImageSelect(event, 'camera')}
            className="file-input"
            style={{ display: 'none' }}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={event => onImageSelect(event, 'gallery')}
            className="file-input"
            style={{ display: 'none' }}
          />
        </>
      )}
    </div>
  );
};

export default UploadSection;
